import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { signOutSuccess } from 'store/slices/authSlice';
import store from '../store';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import { notification } from 'antd';
import { toCamel } from 'utils/toCamel';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';

const convertResponseToCamelCase = (response) => {
  if (
    response.data &&
    response.headers['content-type'] &&
    response.headers['content-type'].startsWith('application/json')
  ) {
    response.data = toCamel(response.data);
  }
  return response.data;
};

const unauthorizedCode = [401, 403];
let isAuthErrorHandled = false;

const service = axios.create({
  baseURL: API_BASE_URL + '/api/v1/',
  timeout: 60000,
});

// Config
const TOKEN_PAYLOAD_KEY = 'Authorization';

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;
    const token = `Bearer ${jwtToken}`;
    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = token;
    }

    return config;
  },
  (error) => {
    notification.error({
      message: 'Service Error',
    });
    return Promise.reject(error);
  },
);

// API Response interceptor
service.interceptors.response.use(
  (response) => {
    isAuthErrorHandled = false; // Reset the flag on a successful response
    return convertResponseToCamelCase(response);
  },
  (error) => {
    if (!error?.response) {
      notification.error({
        message: extractApiErrorMessage(
          error,
          'Error de conexión con el servicio',
        ),
      });
      return Promise.reject(error);
    }

    const status = error.response.status;

    // Handle authentication errors only once
    if (unauthorizedCode.includes(status)) {
      if (!isAuthErrorHandled) {
        isAuthErrorHandled = true;
        localStorage.removeItem(AUTH_TOKEN);
        store.dispatch(signOutSuccess());
        notification.error({
          message: 'Falla de autenticación',
          description: 'Por favor ingrese nuevamente',
        });
      }
      return Promise.reject(error);
    }

    const statusDefaults = {
      404: 'Not Found',
      405: 'Method Not Allowed',
      409: 'Conflicto al procesar la solicitud',
      500: 'Internal Server Error',
      508: 'Time Out',
    };

    const notificationParam = {
      message:
        extractApiErrorMessage(error) ||
        statusDefaults[status] ||
        'Service error',
    };

    if (
      status === 422 &&
      error.response?.data?.detail?.[0]?.type === 'uuid_parsing'
    ) {
      notificationParam.message = 'Formato de ID inválido';
      notificationParam.description =
        'El ID proporcionado no tiene un formato válido o no se encuentra en la base de datos';
    }

    // Permite que un request opte por manejar el error por su cuenta.
    // `suppressErrorNotification: true` silencia todo, o una lista de códigos
    // (ej: [404]) silencia solo esos estados.
    const suppress = error.config?.suppressErrorNotification;
    const shouldSuppress =
      suppress === true || (Array.isArray(suppress) && suppress.includes(status));

    if (!shouldSuppress) {
      notification.error(notificationParam);
    }

    return Promise.reject(error);
  },
);

export default service;
